import { accentInkFor, type AccentColor } from '@aysar/contracts';

import { type ResolvedTheme } from '@/shared/store/reducers/preferences/preferences.types';
import { hexToHslChannels } from '@/shared/utils/hex-to-hsl/hex-to-hsl.util';

const ACCENT = '--accent';
const ACCENT_FOREGROUND = '--accent-foreground';

/**
 * Applies `brand.accentColor` as the `accent` token pair on `<html>` (P28).
 *
 * The variable IS the token (U6): components keep saying `bg-accent`, and what
 * changes is the value behind it. `''` clears the inline declarations so the
 * stylesheet's per-theme values return — the override is never a copy of them.
 * The ink is not a colour of its own: it is whichever of the palette's two
 * neutrals reads on the accent, chosen by the contrast rule both clients share,
 * and expressed as the theme's existing `--foreground` / `--background` so a
 * dark accent under a dark theme still gets a light ink.
 */
export function applyAccentToDocument(accent: AccentColor, theme: ResolvedTheme): void {
  const { style } = document.documentElement;

  if (accent === '') {
    style.removeProperty(ACCENT);
    style.removeProperty(ACCENT_FOREGROUND);
    return;
  }

  const wantsDarkInk = accentInkFor(accent) === 'dark';
  const darkInk = theme === 'dark' ? 'var(--background)' : 'var(--foreground)';
  const lightInk = theme === 'dark' ? 'var(--foreground)' : 'var(--background)';

  style.setProperty(ACCENT, hexToHslChannels(accent));
  style.setProperty(ACCENT_FOREGROUND, wantsDarkInk ? darkInk : lightInk);
}
